// A new password: the current one (at the first sign-in: the one-time password from the admin) and the new one,
// at least 12 characters, with a strength hint. Used by the sign-in step and by My account.
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Field } from '@the-kit/ui'
import { ChangePasswordInput, type Session } from '@/api/schemas'
import { ErrorText } from '@/components/ErrorText'
import { PasswordStrength } from '@/components/PasswordStrength'
import { useChangePassword } from '../hooks'

interface ChangePasswordFormProps {
  currentLabel: string
  submitText: string
  onSaved?: (session: Session) => void
}

export function ChangePasswordForm({ currentLabel, submitText, onSaved }: ChangePasswordFormProps) {
  const change = useChangePassword()
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(ChangePasswordInput),
    defaultValues: { current: '', next: '' },
  })
  const next = useWatch({ control: form.control, name: 'next' })
  const { errors } = form.formState
  const submit = form.handleSubmit((input) =>
    change.mutate(input, {
      onSuccess: (session) => {
        form.reset()
        onSaved?.(session)
      },
    })
  )
  return (
    <form onSubmit={submit} noValidate data-testid="change-password-form">
      <Field label={currentLabel} error={errors.current?.message}>
        {(id) => (
          <input
            id={id}
            className="input"
            type="password"
            autoComplete="current-password"
            {...form.register('current')}
            data-testid="password-current"
          />
        )}
      </Field>
      <Field label="New password (at least 12 characters)" error={errors.next?.message}>
        {(id) => (
          <input
            id={id}
            className="input"
            type="password"
            autoComplete="new-password"
            {...form.register('next')}
            data-testid="password-new"
          />
        )}
      </Field>
      <PasswordStrength password={next} />
      <ErrorText error={change.error} />
      <button
        type="submit"
        className="btn btn-primary mt-3 w-full"
        disabled={change.isPending}
        data-testid="password-save"
      >
        {submitText}
      </button>
    </form>
  )
}
